import { Drawer } from "@mui/material";
import { FieldErrors, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import TextField from "@mui/material/TextField";
import { useSelector, useDispatch } from "react-redux";
import { ThemeProvider } from "@mui/material";
import Select from "@mui/material/Select";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";

import useUpdateEntity from "@/hooks/useUpdateEntity";
import useCreateNewEntity from "@/hooks/useCreateNewEntity";
import { RootState } from "@/store/store";
import Title from "@/shared/components/Title/Title";
import Button from "@/shared/components/Button/Button";
import CloseCross from "@/shared/components/CloseCross/CloseCross";
import { inputStyles } from "@/utils/constants";
import { themeSelector } from "@/utils/constants";
import userValidationSchema from "./validationSchemas";
import { updateUserData } from "@/store/users/users";

import { ISearchDrawerProps } from "@/shared/interfaces/ui";
import { ICreateUser, IUpdateUser } from "@/shared/interfaces/patientPage";

export default function UsersDrawerAddUser({
    toggleDrawer,
    open,
    isEditEntity,
}: ISearchDrawerProps) {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const doctorData = useSelector(
        (state: RootState) => state.users.selectedDoctor,
    );

    const isLocalLoading = useSelector(
        (state: RootState) => state.ui.isLocalLoading,
    );

    const { handleCreate } = useCreateNewEntity();
    const { handleUpdate } = useUpdateEntity();

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        setValue,
    } = useForm<ICreateUser | IUpdateUser>({
        resolver: yupResolver(
            userValidationSchema(isEditEntity ? isEditEntity : false),
        ),
        defaultValues: isEditEntity
            ? {
                  name: doctorData.name,
                  email: doctorData.email,
                  roleInClinic: doctorData.roleInClinic,
              }
            : {
                  name: "",
                  email: "",
                  password: "",
                  passwordConfirm: "",
                  roleInClinic: "doctor",
              },
    });

    const onSubmit = async (data: ICreateUser | IUpdateUser): Promise<void> => {
        function handleClose() {
            toggleDrawer(false);
            reset();
        }
        function handleCloseAndUpdate() {
            handleClose();
            reset();
            dispatch(updateUserData(data));
        }

        if (!isEditEntity) {
            const createUserData = data as ICreateUser;
            await handleCreate({
                entity: "users/createemployee",
                data: createUserData,
                message: "Користувача додано успішно",
                onSuccess: () => handleClose(),
            });
        } else {
            const updateUserData = data as IUpdateUser;
            await handleUpdate({
                entity: "users",
                id: doctorData._id ? doctorData._id : "",
                data: updateUserData,
                onSuccess: handleCloseAndUpdate,
            });
        }
    };

    function onCloseDrawer() {
        if (!isLocalLoading) {
            toggleDrawer(false);
            reset();
        }
    }

    useEffect(() => {
        if (isEditEntity && doctorData?._id) {
            reset({
                name: doctorData.name || "",
                email: doctorData.email || "",
                roleInClinic: doctorData.roleInClinic,
            });
        }
    }, [isEditEntity, doctorData, reset]);

    return (
        <Drawer open={open} onClose={onCloseDrawer} anchor='right'>
            <div className='drawer_container'>
                <div className='drawer_title_container drawer_app_item'>
                    <Title
                        tag='h2'
                        text={`${
                            isEditEntity ? "Редагувати" : "Додати"
                        } ${"користувача"} `}
                    />
                    <CloseCross onClick={onCloseDrawer} />
                </div>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className='drawer_container_create_entity'
                >
                    <div className='drawer_form_container'>
                        <TextField
                            type='text'
                            id='name'
                            label='ПІБ користувача'
                            variant='outlined'
                            {...register("name")}
                            error={!!errors.name}
                            helperText={errors.name ? errors.name.message : ""}
                            sx={{
                                ...inputStyles.root,
                                mb: "16px",
                                width: "100%",
                            }}
                        />

                        <TextField
                            type='email'
                            {...register("email")}
                            error={!!errors.email}
                            id='outlined-error-helper-text'
                            label='Електронна пошта'
                            variant='outlined'
                            sx={{
                                ...inputStyles.root,
                                mb: "16px",
                                width: "100%",
                            }}
                            helperText={
                                errors.email ? errors.email.message : ""
                            }
                        />
                        {isEditEntity ? null : (
                            <TextField
                                type='password'
                                id='password'
                                label='Пароль'
                                variant='outlined'
                                {...register("password")}
                                helperText={
                                    !isEditEntity &&
                                    (errors as FieldErrors<ICreateUser>)
                                        ?.password?.message
                                        ? (errors as FieldErrors<ICreateUser>)
                                              ?.password?.message
                                        : ""
                                }
                                error={
                                    !isEditEntity &&
                                    !!(errors as FieldErrors<ICreateUser>)
                                        ?.password
                                }
                                sx={{
                                    ...inputStyles.root,
                                    mb: "16px",
                                    width: "100%",
                                }}
                            />
                        )}

                        {isEditEntity ? null : (
                            <TextField
                                type='password'
                                id='passporComfirm'
                                label='Підтвердіть пароль'
                                variant='outlined'
                                {...register("passwordConfirm")}
                                error={
                                    !isEditEntity &&
                                    !!(errors as FieldErrors<ICreateUser>)
                                        ?.passwordConfirm
                                }
                                helperText={
                                    !isEditEntity &&
                                    (errors as FieldErrors<ICreateUser>)
                                        ?.passwordConfirm?.message
                                        ? (errors as FieldErrors<ICreateUser>)
                                              ?.passwordConfirm?.message
                                        : ""
                                }
                                sx={{
                                    ...inputStyles.root,
                                    mb: "16px",
                                    width: "100%",
                                }}
                            />
                        )}

                        <ThemeProvider theme={themeSelector}>
                            <Box sx={{ minWidth: 120 }}>
                                <FormControl fullWidth>
                                    <InputLabel
                                        id='demo-simple-select-label'
                                        sx={{
                                            color: "#47586E",
                                        }}
                                    >
                                        {"Роль в клініці"}
                                    </InputLabel>
                                    <Select
                                        {...register("roleInClinic")}
                                        value={watch("roleInClinic")}
                                        onChange={e =>
                                            setValue(
                                                "roleInClinic",
                                                e.target.value as
                                                    | "doctor"
                                                    | "admin",
                                                { shouldValidate: true },
                                            )
                                        }
                                        label={"Роль в клініці"}
                                        MenuProps={{
                                            disableScrollLock: true,
                                            PaperProps: {
                                                sx: {
                                                    overflowY: "auto",
                                                    marginTop: 1,
                                                },
                                            },
                                        }}
                                        sx={{
                                            "&:hover .MuiOutlinedInput-notchedOutline":
                                                {
                                                    borderColor: "#BCC3CD",
                                                },
                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                {
                                                    borderColor: "#1367B5",
                                                },
                                        }}
                                    >
                                        <MenuItem value={"doctor"}>
                                            Лікар
                                        </MenuItem>
                                        <MenuItem value={"admin"}>
                                            Адміністратор
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </ThemeProvider>
                    </div>
                    <Button
                        text={isEditEntity ? "Зберегти" : "Додати"}
                        variant='approved'
                        type='submit'
                        disabled={isLocalLoading}
                        isLoading={isLocalLoading}
                    />
                </form>
            </div>
        </Drawer>
    );
}
